/**
 * ModuleGuard — wrapper który renderuje children tylko jeśli tenant ma aktywną
 * subskrypcję modułu `moduleCode`. W przeciwnym razie pokazuje fallback
 * „Moduł nieaktywny" z CTA (F7b T7b.7).
 *
 * Używane dla route'ów modułowych w T7b.8+:
 *   <Route path="/modules/workflow/*" element={
 *     <ModuleGuard moduleCode="workflow"><WorkflowRouter /></ModuleGuard>
 *   } />
 *
 * Loading → skeleton (nie centered spinner — żeby layout nie skakał przy nawigacji).
 * Error → komunikat + retry (TanStack Query retry raz defaultowo z `createQueryClient`).
 *
 * CTA „Skontaktuj się z nami" = mailto:support — dopóki F8 nie doda self-service
 * activation (tenant klika „Dokup moduł" → payment flow). Wtedy CTA fliphnie na link
 * do `/settings/modules` (T7b.8 ModulesTab placeholder).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useMyModules } from '@/lib/use-my-modules';

export interface ModuleGuardProps {
  moduleCode: string;
  children: ReactNode;
}

export function ModuleGuard({ moduleCode, children }: ModuleGuardProps) {
  const { data, isLoading, isError } = useMyModules();

  if (isLoading) return <ModuleGuardLoading />;
  if (isError || !data) return <ModuleGuardError />;

  const isActive = data.subscriptions.some((s) => s.code === moduleCode);
  if (isActive) return <>{children}</>;

  const catalogEntry = data.catalog.find((c) => c.code === moduleCode);
  return <ModuleInactiveNotice moduleName={catalogEntry?.name ?? moduleCode} />;
}

/** Stan „sprawdzam dostęp" — skeleton (wspólny z `ModuleAreaGuard`). */
export function ModuleGuardLoading() {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center p-8"
      role="status"
      aria-label={t('tenant.moduleGuard.loading')}
      data-testid="module-guard-loading"
    >
      <div className="space-y-3">
        <div className="h-6 w-48 animate-pulse rounded bg-bg-tertiary" />
        <div className="h-4 w-32 animate-pulse rounded bg-bg-tertiary" />
      </div>
    </div>
  );
}

/** Stan „nie udało się sprawdzić dostępu" (wspólny z `ModuleAreaGuard`). */
export function ModuleGuardError() {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center p-8"
      data-testid="module-guard-error"
    >
      <div
        role="alert"
        className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
      >
        {t('tenant.moduleGuard.errorBody')}
      </div>
    </div>
  );
}

/**
 * Plansza „Moduł X jest nieaktywny" + kontakt. Wspólna dla `ModuleGuard` (jeden moduł)
 * i `ModuleAreaGuard` (dowolny moduł obszaru — wtedy `moduleName` = nazwa obszaru).
 */
export function ModuleInactiveNotice({ moduleName }: { moduleName: string }) {
  const { t } = useTranslation();
  return (
    <main
      className="flex min-h-[60vh] items-center justify-center p-8"
      data-testid="module-guard-inactive"
    >
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 text-center shadow">
        <h1 className="text-xl font-semibold text-text-primary">
          {t('tenant.moduleGuard.inactiveTitle', { moduleName })}
        </h1>
        <p className="mt-2 text-sm text-text-tertiary">{t('tenant.moduleGuard.inactiveBody')}</p>
        <p className="mt-6 text-sm text-text-secondary">
          {t('tenant.moduleGuard.contactPrefix')}{' '}
          <a
            href="mailto:support@saas.pl"
            className="text-accent-600 underline hover:text-accent-700"
          >
            support@saas.pl
          </a>
        </p>
      </div>
    </main>
  );
}
